import { JWT, UserData } from "types";

function getLocalStorage<T>(key: string): T | null {
  const item = localStorage.getItem(key);
  return item ? (JSON.parse(item) as T) : null;
}

function setLocalStorage<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
}

function removeLocalStorage(key: string): void {
  localStorage.removeItem(key);
}

// `platform`/`tenant`/`configId`/`applicationId` are app/client context
// (which shell embedded the webview, which tenant/app this session belongs
// to), not session data — they must survive a logout so the next page
// (including the redirect straight after logout) still resolves the same
// Strapi config instead of falling back to the env-level default.
export function clearLocalStorage(): void {
  const platform = getPlatform();
  const tenant = getTenant();
  const configId = getConfigId();
  const applicationId = getApplicationId();
  localStorage.clear();
  if (platform) setPlatform(platform);
  if (tenant) setTenant(tenant);
  if (configId) setConfigId(configId);
  if (applicationId) setApplicationId(applicationId);
}

const PLATFORM_KEY = "platform";

export function getPlatform(): string | null {
  return getLocalStorage<string>(PLATFORM_KEY);
}

export function setPlatform(platform: string): void {
  setLocalStorage<string>(PLATFORM_KEY, platform);
}

const TENANT_KEY = "tenant";

// Sourced from the tenant's Strapi config (`identifier` field) — see
// components/AppInitializer, which sets this once the config resolves.
export function getTenant(): string | null {
  return getLocalStorage<string>(TENANT_KEY);
}

export function setTenant(tenant: string): void {
  setLocalStorage<string>(TENANT_KEY, tenant);
}

const LANGUAGE_CODE_KEY = "languageCode";

export function getLanguageCode(): string | null {
  return getLocalStorage<string>(LANGUAGE_CODE_KEY);
}

export function setLanguageCode(languageCode: string): void {
  setLocalStorage<string>(LANGUAGE_CODE_KEY, languageCode);
}

const APPLICATION_ID_KEY = "applicationId";

export function getApplicationId(): string | null {
  return getLocalStorage<string>(APPLICATION_ID_KEY);
}

export function setApplicationId(applicationId: string): void {
  setLocalStorage<string>(APPLICATION_ID_KEY, applicationId);
}

const CONFIG_ID_KEY = "configId";

export function getConfigId(): string | null {
  return getLocalStorage<string>(CONFIG_ID_KEY);
}

export function setConfigId(configId: string): void {
  setLocalStorage<string>(CONFIG_ID_KEY, configId);
}

const USER_DATA_KEY = "userData";

const JWT_KEY = "jwt_key";

export function getUserData(): UserData | null {
  return getLocalStorage<UserData>(USER_DATA_KEY);
}

export function setUserData(data: UserData): void {
  setLocalStorage<UserData>(USER_DATA_KEY, data);
}

export function getJwtTokens(): JWT | null {
  return getLocalStorage<JWT>(JWT_KEY);
}

export function setJwtTokens(data: any): void {
  setLocalStorage(JWT_KEY, data);
}

export function getAccessToken(): string {
  const jwtTokens = getJwtTokens();
  return jwtTokens?.token || "";
}

export function removeUserData(): void {
  removeLocalStorage(USER_DATA_KEY);
}

export interface LoginFlowState {
  route?: string;
  email?: string;
  code?: string;
  verificationId?: string;
  step?: number;
  // When the user first attempted login — set on submit, read back once
  // login succeeds (possibly on a later page, e.g. after OTP verification)
  // to compute the `time_to_login` analytics duration.
  loginStartedAt?: number;
}

function flowStateKey(configId: string | null | undefined): string {
  return `loginFlowState:${configId ?? "default"}`;
}

export function getFlowState(configId: string | null | undefined): LoginFlowState {
  return getLocalStorage<LoginFlowState>(flowStateKey(configId)) ?? {};
}

export function setFlowState(configId: string | null | undefined, partial: LoginFlowState): void {
  setLocalStorage<LoginFlowState>(flowStateKey(configId), { ...getFlowState(configId), ...partial });
}

export function clearFlowState(configId: string | null | undefined): void {
  removeLocalStorage(flowStateKey(configId));
}
